import { Card, Space, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { HolderOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { JsonSchemaField } from '@/models/content-block-schema.model';
import { SupportedLocales } from '@/models/localization.model';
import { translatableValueToLocaleMap } from '../../helpers/galleryBlockHelpers';
import { buildDefaultItemForArrayRow, getArrayItemFieldMap } from '../../helpers/schemaDefaults';
import { generateBlockId } from '../../helpers/blockFactory';
import { ObjectFieldsEditor } from './ObjectFieldsEditor';

const ROW_ID = '_rowId';

type GalleryRowProps = {
  id: string;
  item: Record<string, unknown>;
  itemSchema: Record<string, JsonSchemaField>;
  locales: SupportedLocales[];
  onChangeItem: (next: Record<string, unknown>) => void;
  onRemove: () => void;
};

const SortableGalleryRow = ({ id, item, itemSchema, locales, onChangeItem, onRemove }: GalleryRowProps) => {
  const { t } = useTranslation(['contentPages']);
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card
        size="small"
        style={{ marginBottom: 8 }}
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span {...attributes} {...listeners} style={{ cursor: 'grab' }}>
              <HolderOutlined />
            </span>
            <Typography.Text type="secondary">{t('gallery_item_title')}</Typography.Text>
          </span>
        }
        extra={
          <Typography.Link type="danger" onClick={onRemove}>
            {t('remove_item')}
          </Typography.Link>
        }
      >
        <ObjectFieldsEditor
          blockType="gallery"
          properties={itemSchema}
          value={item}
          onChange={onChangeItem}
          locales={locales}
        />
      </Card>
    </div>
  );
};

type TGalleryRootEditor = {
  schema: JsonSchemaField;
  value: unknown[];
  onChange: (next: unknown[]) => void;
  locales: SupportedLocales[];
  maxItems?: number;
  /** Block-level gallery header — used to pre-fill empty `alt` on new rows */
  headerSource?: unknown;
};

export const GalleryRootEditor = ({ schema, value, onChange, locales, maxItems, headerSource }: TGalleryRootEditor) => {
  const { t } = useTranslation(['contentPages']);
  const itemMap = getArrayItemFieldMap(schema);
  const rows = Array.isArray(value) ? value : [];

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  if (!itemMap) {
    return <Typography.Text type="danger">{t('gallery_invalid_schema')}</Typography.Text>;
  }

  const ensureRowIds = (list: unknown[]): Record<string, unknown>[] =>
    list.map((row) => {
      const r = row && typeof row === 'object' && !Array.isArray(row) ? { ...(row as Record<string, unknown>) } : {};
      if (!r[ROW_ID]) {
        r[ROW_ID] = generateBlockId();
      }
      return r;
    });

  const normalized = ensureRowIds(rows);
  const ids = normalized.map((r) => String(r[ROW_ID]));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onChange(arrayMove(normalized, oldIndex, newIndex));
  };

  const addRow = () => {
    if (maxItems !== undefined && normalized.length >= maxItems) return;
    const row = buildDefaultItemForArrayRow(itemMap, locales) as Record<string, unknown>;
    row[ROW_ID] = generateBlockId();
    const headerMap = translatableValueToLocaleMap(headerSource, locales);
    if (row.alt && typeof row.alt === 'object' && !Array.isArray(row.alt)) {
      const alt = { ...(row.alt as Record<string, unknown>) };
      locales.forEach((l) => {
        const v = alt[l];
        if (v === undefined || v === null || String(v).trim() === '') {
          alt[l] = headerMap[l] ?? '';
        }
      });
      row.alt = alt;
    }
    onChange([...normalized, row]);
  };

  const updateRow = (index: number, next: Record<string, unknown>) => {
    const nextRows = [...normalized];
    nextRows[index] = next;
    onChange(nextRows);
  };

  const removeRow = (index: number) => {
    onChange(normalized.filter((_, i) => i !== index));
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {normalized.map((row, index) => (
            <SortableGalleryRow
              key={String(row[ROW_ID])}
              id={String(row[ROW_ID])}
              item={row}
              itemSchema={itemMap}
              locales={locales}
              onChangeItem={(next) => updateRow(index, next)}
              onRemove={() => removeRow(index)}
            />
          ))}
        </SortableContext>
      </DndContext>
      <Typography.Link
        onClick={addRow}
        disabled={maxItems !== undefined && normalized.length >= maxItems}
        title={maxItems !== undefined && normalized.length >= maxItems ? t('gallery_max_reached') : undefined}
      >
        <PlusOutlined /> {t('add_gallery_item')}
      </Typography.Link>
    </Space>
  );
};
